import React, { memo, useMemo } from "react";
import { FocusContext, useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import { SEARCH_MODE } from "./SearchPolicy";
import KeyboardKey from "./KeyboardKey";
import spaceIcon from "../../assets/space.svg";
import eraseIcon from "../../assets/erase.svg";

// Control tokens the view interprets; character keys emit their own char.
export const KEY_TOKENS = {
    SPACE: "{space}",
    DELETE: "{bksp}",
    SEARCH: "{search}",
};

// focusKey of the first key, so the view can grab focus on mount.
export const FIRST_KEY_FOCUS = "kb-key-0-0";

// Alphabetical grid, 6 columns — matches the On-Screen-Guide design.
const CHAR_ROWS = ["ABCDEF", "GHIJKL", "MNOPQR", "STUVWX", "YZ1234", "567890"];

const SPACE_KEY = { label: "Space", value: KEY_TOKENS.SPACE, icon: spaceIcon, wide: true };
const ERASE_KEY = { label: "Erase", value: KEY_TOKENS.DELETE, icon: eraseIcon, wide: true };
const SEARCH_KEY = { label: "Search", value: KEY_TOKENS.SEARCH, full: true };

// Control rows sit ABOVE the letters. Low-end (Enter mode) adds a full-width
// Search key on its own row above Space/Erase; capable devices search as they type.
const buildRows = (showSearchKey) => {
    const control = [];
    if (showSearchKey) control.push([SEARCH_KEY]);
    control.push([SPACE_KEY, ERASE_KEY]);

    const charRows = CHAR_ROWS.map((row) =>
        row.split("").map((ch) => ({ label: ch, value: ch }))
    );
    return [...control, ...charRows];
};

const OnScreenKeyboard = memo(({ mode, onKeyPress }) => {
    const { ref, focusKey } = useFocusable({ focusKey: "epg-keyboard" });
    const rows = useMemo(() => buildRows(mode === SEARCH_MODE.ENTER), [mode]);

    return (
        <FocusContext.Provider value={focusKey}>
            <div ref={ref} className="epgKeyboard">
                {rows.map((row, rowIndex) => (
                    <div key={rowIndex} className="epgKeyboard__row">
                        {row.map((key, keyIndex) => (
                            <KeyboardKey
                                key={key.value}
                                focusKey={`kb-key-${rowIndex}-${keyIndex}`}
                                label={key.label}
                                value={key.value}
                                icon={key.icon}
                                wide={key.wide}
                                full={key.full}
                                onPress={onKeyPress}
                            />
                        ))}
                    </div>
                ))}
            </div>
        </FocusContext.Provider>
    );
});

export default OnScreenKeyboard;
